// src/Dashboard/pages/user/Lexique/hooks/useLexique.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getSettings } from "../../../../../services/settingsServices";
import { getLexique } from "../../../../../services/lexiqueServices";
import { getLexiqueKarate } from "../../../../../services/lexiquekarateServices";

import {
  DEFAULT_UI_STATE,
  DEFAULT_VISIBLE_ROWS,
  FAV_STORAGE_KEY,
  UI_STORAGE_KEY,
  buildShareUrl,
  collatorFR,
  getInitialLexiqueUi,
  getJapanese,
  getLexiqueKey,
  getTranslation,
  isKarateDiscipline,
  makeLexiqueCsv,
  readStorageJson,
  sanitizeFilename,
  strip,
  writeStorageJson,
} from "../utils/lexiqueUtils";

export function useLexique() {
  const [discipline, setDiscipline] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [ui, setUi] = useState(getInitialLexiqueUi);
  const [debouncedQ, setDebouncedQ] = useState(ui.q || "");

  const [favorites, setFavorites] = useState(() =>
    readStorageJson(FAV_STORAGE_KEY, [])
  );

  const [shareCopied, setShareCopied] = useState(false);

  const persistUi = useCallback((next) => {
    setUi(next);
    writeStorageJson(UI_STORAGE_KEY, next);
  }, []);

  const updateUi = useCallback((patch) => {
    setUi((previous) => {
      const shouldResetPage = ["q", "letter", "sort", "favOnly"].some((key) =>
        Object.prototype.hasOwnProperty.call(patch, key)
      );

      const next = {
        ...previous,
        ...patch,
      };

      if (
        shouldResetPage &&
        !Object.prototype.hasOwnProperty.call(patch, "perPage")
      ) {
        next.perPage = DEFAULT_VISIBLE_ROWS;
      }

      writeStorageJson(UI_STORAGE_KEY, next);

      return next;
    });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      updateUi({
        q: debouncedQ,
      });
    }, 250);

    return () => clearTimeout(timer);
  }, [debouncedQ, updateUi]);

  const loadLexique = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const settings = await getSettings();
      const currentDiscipline = settings?.[0]?.discipline_nom || "Judo";

      setDiscipline(currentDiscipline);

      const source = isKarateDiscipline(currentDiscipline)
        ? getLexiqueKarate
        : getLexique;

      const raw = await source();
      setData(Array.isArray(raw) ? raw : []);
    } catch (err) {
      console.error("Erreur chargement lexique :", err);
      setData([]);
      setError("Impossible de charger le lexique pour le moment.");
      toast.error("Erreur lors du chargement du lexique.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLexique();
  }, [loadLexique]);

  const availableLetters = useMemo(() => {
    const set = new Set(
      data
        .map((row) => getJapanese(row).toUpperCase().charAt(0))
        .filter(Boolean)
        .filter((letter) => /[A-ZÀ-ÖØ-Ý]/i.test(letter))
    );

    const letters = Array.from(set);
    letters.sort((a, b) => collatorFR.compare(a, b));

    return ["Toutes", ...letters];
  }, [data]);

  const isFavorite = useCallback(
    (row) => {
      const key = getLexiqueKey(row);
      const legacyKey = getJapanese(row);

      return favorites.includes(key) || favorites.includes(legacyKey);
    },
    [favorites]
  );

  const filtered = useMemo(() => {
    const q = strip(ui.q);
    const letter = ui.letter || "Toutes";

    const rows = data.filter((row) => {
      const japonais = getJapanese(row);
      const traduction = getTranslation(row);

      const matchesQ =
        !q || strip(japonais).includes(q) || strip(traduction).includes(q);

      const matchesLetter =
        letter === "Toutes" ||
        japonais.toUpperCase().startsWith(letter.toUpperCase());

      const matchesFavorite = !ui.favOnly || isFavorite(row);

      return matchesQ && matchesLetter && matchesFavorite;
    });

    rows.sort((a, b) => {
      const jpA = getJapanese(a);
      const jpB = getJapanese(b);
      const frA = getTranslation(a);
      const frB = getTranslation(b);

      if (ui.sort === "fr") {
        return collatorFR.compare(frA, frB) || collatorFR.compare(jpA, jpB);
      }

      return collatorFR.compare(jpA, jpB) || collatorFR.compare(frA, frB);
    });

    return rows;
  }, [data, ui, isFavorite]);

  const visibleRows = useMemo(() => {
    return filtered.slice(0, ui.perPage || DEFAULT_VISIBLE_ROWS);
  }, [filtered, ui.perPage]);

  const groups = useMemo(() => {
    const map = new Map();

    visibleRows.forEach((row) => {
      const japonais = getJapanese(row);
      const letter =
        ui.letter === "Toutes"
          ? japonais.toUpperCase().charAt(0) || "#"
          : ui.letter;

      if (!map.has(letter)) {
        map.set(letter, []);
      }

      map.get(letter).push(row);
    });

    const keys = Array.from(map.keys()).sort((a, b) =>
      collatorFR.compare(a, b)
    );

    return keys.map((letter) => ({
      letter,
      items: map.get(letter),
    }));
  }, [visibleRows, ui.letter]);

  const favoriteRowsCount = useMemo(() => {
    return data.filter((row) => isFavorite(row)).length;
  }, [data, isFavorite]);

  const stats = useMemo(() => {
    return {
      total: data.length,
      filtered: filtered.length,
      favorites: favoriteRowsCount,
      letters: Math.max(availableLetters.length - 1, 0),
    };
  }, [data.length, filtered.length, favoriteRowsCount, availableLetters.length]);

  const resetFilters = useCallback(() => {
    setDebouncedQ("");
    persistUi(DEFAULT_UI_STATE);
  }, [persistUi]);

  const loadMore = useCallback(() => {
    updateUi({
      perPage: (ui.perPage || DEFAULT_VISIBLE_ROWS) + DEFAULT_VISIBLE_ROWS,
    });
  }, [ui.perPage, updateUi]);

  const toggleFavorite = useCallback((row) => {
    const key = getLexiqueKey(row);
    const legacyKey = getJapanese(row);

    setFavorites((previous) => {
      const alreadyFavorite =
        previous.includes(key) || previous.includes(legacyKey);

      const cleaned = previous.filter(
        (item) => item !== key && item !== legacyKey
      );

      const next = alreadyFavorite ? cleaned : [...cleaned, key];

      writeStorageJson(FAV_STORAGE_KEY, next);

      return next;
    });
  }, []);

  const copyShare = useCallback(async () => {
    try {
      const url = buildShareUrl(ui);

      if (!url) return;

      await navigator.clipboard.writeText(url);

      setShareCopied(true);
      toast.success("Lien copié.");

      window.setTimeout(() => {
        setShareCopied(false);
      }, 1200);
    } catch (err) {
      console.error("Erreur copie lien :", err);
      toast.error("Impossible de copier le lien.");
    }
  }, [ui]);

  const exportCSV = useCallback(() => {
    try {
      if (!filtered.length) {
        toast.info("Aucune ligne à exporter.");
        return;
      }

      const csv = makeLexiqueCsv(filtered);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `lexique_${sanitizeFilename(discipline || "judo")}.csv`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      URL.revokeObjectURL(url);

      toast.success("Export CSV généré.");
    } catch (err) {
      console.error("Erreur export CSV :", err);
      toast.error("Erreur lors de l’export CSV.");
    }
  }, [filtered, discipline]);

  return {
    discipline,
    loading,
    error,

    data,
    filtered,
    visibleRows,
    groups,
    availableLetters,
    stats,

    ui,
    debouncedQ,
    setDebouncedQ,
    updateUi,
    resetFilters,
    loadMore,

    favorites,
    isFavorite,
    toggleFavorite,

    shareCopied,
    copyShare,
    exportCSV,
    refresh: loadLexique,
  };
}